import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link as LinkIcon, Upload, RotateCcw, RotateCw, Grid2x2, X, Pencil, Shuffle, Images, Crop } from 'lucide-react';
import PhotoEditModal from './PhotoEditModal';
import GalleryPicker from './GalleryPicker';
import AlphaColorPicker from './AlphaColorPicker';
import { BackgroundRemover } from './BackgroundRemover';
import { toast } from 'sonner';
import { normalizeUrl } from '../lib/normalizeUrl';

const INTERVALS = [
  { value: 0, label: 'On page load only' },
  { value: 5, label: 'Every 5 seconds' },
  { value: 10, label: 'Every 10 seconds' },
  { value: 30, label: 'Every 30 seconds' },
  { value: 60, label: 'Every minute' },
];

// An image that admins can edit by right-clicking. Non-admins see a plain image.
// A fixed override URL is stored in settings.images[imgKey]; a tag-rotation config
// {tag, interval} is stored in settings.imageRotations[imgKey]. When a rotation is
// set the image randomly shows an approved gallery photo carrying that tag, and can
// optionally auto-cycle on a timer. Rotation takes precedence over a fixed override.
export default function EditableImage({ imgKey, src, alt, className, style, wrapperClassName, fallback }) {
  const {
    settings, setImageOverride, clearImageOverride, uploadImage, isAdmin,
    setImageRotation, clearImageRotation, getPhotoTags, getPhotosByTag,
    setImageLayout, clearImageLayout, mediaUrl,
  } = useAuth();
  const override = (settings && settings.images && settings.images[imgKey]) || null;
  const rotation = (settings && settings.imageRotations && settings.imageRotations[imgKey]) || null;
  const layout = (settings && settings.imageLayouts && settings.imageLayouts[imgKey]) || null;

  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [editSrc, setEditSrc] = useState('');
  const fileRef = useRef(null);
  const posBoxRef = useRef(null);
  const [galleryOpen, setGalleryOpen] = useState(false);

  // Rotation state
  const [rotPhotos, setRotPhotos] = useState([]);
  const [rotIndex, setRotIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  // Rotation picker state (admin modal)
  const [tags, setTags] = useState([]);
  const [pickTag, setPickTag] = useState('');
  const [pickInterval, setPickInterval] = useState(0);

  // Display/layout picker state
  const [fit, setFit] = useState('contain');
  const [pos, setPos] = useState('center');
  const [lw, setLw] = useState('');
  const [lh, setLh] = useState('');
  const [rotate, setRotate] = useState(0);
  const [bgTransparent, setBgTransparent] = useState(false);
  const [bgColor, setBgColor] = useState('');

  // Default: scale to fit the display area, keeping aspect ratio, unless a layout overrides it.
  const layoutStyle = layout ? {
    objectFit: layout.fit || 'contain',
    ...(layout.position ? { objectPosition: layout.position } : {}),
    ...(layout.width ? { width: layout.width } : {}),
    ...(layout.height ? { height: layout.height } : {}),
    ...(layout.rotate ? { transform: `rotate(${layout.rotate}deg)` } : {}),
    ...(layout.bgColor ? { background: layout.bgColor } : (layout.transparentBg ? { background: 'transparent' } : {})),
  } : { objectFit: 'contain' };

  // Edit mode: drag the corner handle to size the display area (saved as width/height in px).
  const imgRef = useRef(null);
  const [resizing, setResizing] = useState(null); // {w,h}
  const startResize = (e) => {
    e.preventDefault(); e.stopPropagation();
    const el = imgRef.current; if (!el) return;
    const rect = el.getBoundingClientRect();
    const sx = e.clientX, sy = e.clientY, sw = rect.width, sh = rect.height;
    const move = (ev) => setResizing({ w: Math.max(40, Math.round(sw + ev.clientX - sx)), h: Math.max(30, Math.round(sh + ev.clientY - sy)) });
    const up = async (ev) => {
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
      const w = Math.max(40, Math.round(sw + ev.clientX - sx)); const h = Math.max(30, Math.round(sh + ev.clientY - sy));
      setResizing(null);
      if (Math.abs(w - sw) < 3 && Math.abs(h - sh) < 3) return;
      try { await setImageLayout(imgKey, { ...(layout || { fit: 'contain', position: 'center' }), width: `${w}px`, height: `${h}px` }); toast.success(`Display area set to ${w}×${h}px.`); }
      catch { toast.error('Could not save the display size.'); }
    };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };

  const rotTag = rotation && rotation.tag;
  const rotInterval = (rotation && Number(rotation.interval)) || 0;

  // Load the photo pool whenever the active rotation tag changes.
  useEffect(() => {
    let cancelled = false;
    if (!rotTag) { setRotPhotos([]); return; }
    getPhotosByTag(rotTag)
      .then((rows) => {
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows.filter((r) => r && r.url) : [];
        setRotPhotos(list);
        setRotIndex(list.length ? Math.floor(Math.random() * list.length) : 0);
      })
      .catch(() => { if (!cancelled) setRotPhotos([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rotTag]);

  // Auto-cycle on a timer with a soft crossfade.
  useEffect(() => {
    if (!rotTag || rotInterval <= 0 || rotPhotos.length < 2) return;
    const id = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setRotIndex((i) => (i + 1) % rotPhotos.length);
        setVisible(true);
      }, 350);
    }, rotInterval * 1000);
    return () => clearInterval(id);
  }, [rotTag, rotInterval, rotPhotos.length]);

  const rotationUrl = (rotTag && rotPhotos.length) ? rotPhotos[rotIndex % rotPhotos.length].url : null;
  const effective = mediaUrl(rotationUrl || override || src);

  const openModal = useCallback(() => {
    setUrl(override || '');
    setErr('');
    setPickTag(rotTag || '');
    setPickInterval(rotInterval);
    setFit((layout && layout.fit) || 'contain');
    setPos((layout && layout.position) || 'center');
    setLw((layout && layout.width) || '');
    setLh((layout && layout.height) || '');
    setRotate((layout && layout.rotate) || 0);
    setBgTransparent(!!(layout && layout.transparentBg));
    setBgColor((layout && layout.bgColor) || (layout && layout.transparentBg ? 'transparent' : ''));
    setOpen(true);
    if (isAdmin) getPhotoTags().then((t) => setTags(Array.isArray(t) ? t : [])).catch(() => setTags([]));
  }, [override, rotTag, rotInterval, isAdmin, getPhotoTags]);

  const onContext = (e) => {
    if (!isAdmin) return;
    e.preventDefault();
    openModal();
  };

  const saveUrl = async () => {
    if (!url.trim()) return;
    setBusy(true); setErr('');
    try {
      await setImageOverride(imgKey, normalizeUrl(url), { clearRotation: true });
      setOpen(false);
    } catch (e) { setErr('Could not save. Are you signed in as admin?'); }
    setBusy(false);
  };

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setErr('');
    const reader = new FileReader();
    reader.onload = () => { setEditSrc(reader.result); setOpen(false); };
    reader.onerror = () => setErr('Could not read that image.');
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const onEditConfirm = async (dataUrl) => {
    setBusy(true); setErr('');
    try {
      const newUrl = await uploadImage(dataUrl, dataUrl.startsWith('data:image/png') ? 'image/png' : 'image/jpeg');
      await setImageOverride(imgKey, newUrl, { clearRotation: true });
      setEditSrc('');
      setOpen(false);
    } catch (e2) { setErr('Upload failed. Try a smaller image.'); }
    setBusy(false);
  };

  const reset = async () => {
    setBusy(true); setErr('');
    try { await clearImageOverride(imgKey); setOpen(false); }
    catch (e) { setErr('Could not reset.'); }
    setBusy(false);
  };

  const saveRotation = async () => {
    if (!pickTag) { setErr('Choose a tag to rotate.'); return; }
    setBusy(true); setErr('');
    try {
      await setImageRotation(imgKey, { tag: pickTag, interval: Number(pickInterval) || 0 });
      setOpen(false);
    } catch (e) { setErr('Could not save rotation.'); }
    setBusy(false);
  };

  const turnOffRotation = async () => {
    setBusy(true); setErr('');
    try { await clearImageRotation(imgKey); setOpen(false); }
    catch (e) { setErr('Could not turn off rotation.'); }
    setBusy(false);
  };

  const saveLayout = async () => {
    setBusy(true); setErr('');
    try {
      await setImageLayout(imgKey, { fit, position: pos, width: lw.trim(), height: lh.trim(), rotate, bgColor: bgColor || null, transparentBg: bgColor === 'transparent' });
      setOpen(false);
    } catch (e) { setErr('Could not save the display settings.'); }
    setBusy(false);
  };

  const resetLayout = async () => {
    setBusy(true); setErr('');
    try { await clearImageLayout(imgKey); setOpen(false); }
    catch (e) { setErr('Could not reset display settings.'); }
    setBusy(false);
  };

  const onPosDrag = (e) => {
    const box = posBoxRef.current;
    if (!box) return;
    const rect = box.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, Math.round(((e.clientX - rect.left) / rect.width) * 100)));
    const y = Math.max(0, Math.min(100, Math.round(((e.clientY - rect.top) / rect.height) * 100)));
    setPos(`${x}% ${y}%`);
  };
  const _pm = /(-?\d+)%\s+(-?\d+)%/.exec(pos || '');
  const markX = _pm ? Number(_pm[1]) : 50;
  const markY = _pm ? Number(_pm[2]) : 50;

  return (
    <div className={`relative ${wrapperClassName || ''}`} data-testid={`editable-image-${imgKey}`}>
      {editSrc && (
        <PhotoEditModal src={editSrc} onCancel={() => setEditSrc('')} onConfirm={onEditConfirm} />
      )}
      {effective ? (
        <img
          ref={imgRef}
          src={effective}
          alt={alt}
          className={className}
          style={{ ...(style || {}), ...layoutStyle, ...(resizing ? { width: resizing.w, height: resizing.h, maxWidth: 'none', maxHeight: 'none' } : {}), ...(rotInterval > 0 ? { transition: 'opacity 350ms ease', opacity: visible ? 1 : 0 } : {}) }}
          onContextMenu={onContext}
          data-testid={rotationUrl ? `rotating-image-${imgKey}` : undefined}
        />
      ) : (
        <span onContextMenu={onContext} className={isAdmin ? 'block cursor-context-menu' : 'block'}>{fallback}</span>
      )}
      {isAdmin && rotation && (
        <span
          data-testid={`rotation-badge-${imgKey}`}
          title={`Rotating: #${rotation.tag}`}
          className="absolute top-1 left-1 flex items-center gap-1 px-2 py-1 rounded text-[11px] font-title shadow"
          style={{ background: 'rgba(122,86,44,0.92)', color: 'var(--cream)' }}
        >
          <Shuffle size={12} /> #{rotation.tag}
        </span>
      )}
      {isAdmin && effective && (
        <span
          role="button"
          title="Drag to size the display area"
          data-testid={`resize-handle-${imgKey}`}
          onPointerDown={startResize}
          className="absolute bottom-1 right-1 w-4 h-4 rounded-sm cursor-nwse-resize shadow"
          style={{ background: 'rgba(201,161,59,0.95)', border: '1px solid rgba(0,0,0,0.3)', touchAction: 'none' }}
        />
      )}
      {resizing && <span className="absolute bottom-6 right-1 text-[10px] px-1.5 py-0.5 rounded font-title" style={{ background: 'rgba(0,0,0,0.75)', color: 'white' }} data-testid={`resize-readout-${imgKey}`}>{resizing.w}×{resizing.h}</span>}
      {isAdmin && (
        <button
          type="button"
          data-testid={`edit-image-icon-${imgKey}`}
          title="Edit image"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); openModal(); }}
          className="absolute top-1 right-1 flex items-center gap-1 px-2 py-1 rounded text-[11px] font-title opacity-80 hover:opacity-100 transition-opacity shadow"
          style={{ background: 'rgba(21,56,91,0.92)', color: 'var(--cream)' }}
        >
          <Pencil size={12} /> Edit
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={() => !busy && setOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-title text-xl" style={{ color: 'var(--maroon)' }}>Image options</h3>
              <button onClick={() => setOpen(false)} disabled={busy}><X size={20} /></button>
            </div>
            {effective ? (
              <div className="rounded overflow-hidden mb-4 bg-black/5" style={{ border: '3px solid var(--sage)' }}>
                <img src={effective} alt="preview" className="w-full h-36 object-contain" data-testid={`image-options-preview-${imgKey}`} />
              </div>
            ) : (
              <p className="text-sm mb-4" style={{ color: 'var(--olive)' }}>No custom image set yet — paste a URL, upload one, or rotate by tag.</p>
            )}

            {/* --- Edit the image itself --- */}
            {effective && (
              <button onClick={() => { setEditSrc(effective); setOpen(false); }} disabled={busy} data-testid={`image-edit-current-${imgKey}`}
                className="w-full mb-4 flex items-center justify-center gap-2 px-4 py-2.5 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>
                <Crop size={15} /> Edit image — crop, rotate, flip &amp; adjust
              </button>
            )}

            {/* --- Transparency --- */}
            {effective && (
              <BackgroundRemover
                src={effective}
                imgKey={imgKey}
                onApplied={async (newUrl) => { await setImageOverride(imgKey, newUrl, { clearRotation: true }); toast.success('Background removed — the transparent version is now in use.'); }}
              />
            )}

            {/* --- Display & size --- */}
            <div className="rounded-lg p-3 mb-4" style={{ background: 'rgba(21,56,91,0.06)', border: '1px solid rgba(21,56,91,0.2)' }} data-testid={`image-layout-${imgKey}`}>
              <div className="flex items-center gap-1.5 mb-1">
                <Images size={15} style={{ color: 'var(--navy)' }} />
                <span className="font-title text-sm" style={{ color: 'var(--navy)' }}>Display &amp; size</span>
              </div>
              <p className="text-xs mb-2" style={{ color: 'var(--olive)' }}>Scale the image to fill its frame, and resize or reposition the display area.</p>
              <div className="flex items-center gap-1.5 mb-3 flex-wrap">
                <button onClick={() => setRotate((r) => ((r - 90 + 360) % 360))} title="Rotate left" data-testid={`layout-rotate-left-${imgKey}`} className="p-2 rounded bg-white" style={{ border: '1px solid rgba(0,0,0,0.15)', color: 'var(--navy)' }}><RotateCcw size={15} /></button>
                <button onClick={() => setRotate((r) => ((r + 90) % 360))} title="Rotate right" data-testid={`layout-rotate-right-${imgKey}`} className="p-2 rounded bg-white" style={{ border: '1px solid rgba(0,0,0,0.15)', color: 'var(--navy)' }}><RotateCw size={15} /></button>
                <span className="text-xs mx-1" style={{ color: 'var(--olive)' }}>{rotate}&deg;</span>
                <span className="text-xs ml-1 inline-flex items-center gap-1" title="Colour shown behind the graphic inside its frame (does not alter the image itself)" style={{ color: 'var(--olive)' }}><Grid2x2 size={13} /> Frame fill</span>
                <AlphaColorPicker value={bgColor || 'transparent'} onChange={(val) => { setBgColor(val); setBgTransparent(val === 'transparent'); }} testId={`layout-bg-color-${imgKey}`} />
                <span className="text-xs" style={{ color: 'var(--olive)' }}>{!bgColor || bgColor === 'transparent' ? 'None' : 'Custom colour'}</span>
              </div>
              {effective && (
                <>
                  <label className="block text-xs font-title mb-1" style={{ color: 'var(--navy)' }}>Reposition — drag the marker over the image</label>
                  <div ref={posBoxRef} onPointerDown={onPosDrag} onPointerMove={(e) => e.buttons === 1 && onPosDrag(e)}
                    data-testid={`layout-drag-${imgKey}`} className="relative w-full rounded overflow-hidden mb-2 cursor-move select-none"
                    style={{ height: 120, background: 'rgba(0,0,0,0.06)', touchAction: 'none' }}>
                    <img src={effective} alt="" className="w-full h-full pointer-events-none" style={{ objectFit: fit, objectPosition: pos, transform: rotate ? `rotate(${rotate}deg)` : undefined, background: bgColor && bgColor !== 'transparent' ? bgColor : (bgColor === 'transparent' ? 'transparent' : undefined) }} />
                    <span className="absolute w-4 h-4 rounded-full border-2 border-white shadow pointer-events-none" style={{ background: 'var(--maroon)', left: `calc(${markX}% - 8px)`, top: `calc(${markY}% - 8px)` }} />
                  </div>
                </>
              )}
              <label className="block text-xs font-title mb-1" style={{ color: 'var(--navy)' }}>Scaling</label>
              <select value={fit} onChange={(e) => setFit(e.target.value)} data-testid={`layout-fit-${imgKey}`} className="w-full px-3 py-2 rounded border text-sm mb-2 bg-white" style={{ borderColor: 'rgba(0,0,0,0.2)' }}>
                <option value="contain">Fit whole image, keep aspect ratio (default)</option>
                <option value="cover">Fill the frame (crop to fit)</option>
                <option value="fill">Stretch to frame</option>
              </select>
              <label className="block text-xs font-title mb-1" style={{ color: 'var(--navy)' }}>Focus / position</label>
              <select value={pos} onChange={(e) => setPos(e.target.value)} data-testid={`layout-pos-${imgKey}`} className="w-full px-3 py-2 rounded border text-sm mb-2 bg-white" style={{ borderColor: 'rgba(0,0,0,0.2)' }}>
                {['center', 'top', 'bottom', 'left', 'right', 'top left', 'top right', 'bottom left', 'bottom right'].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <div className="flex gap-2 mb-2">
                <div className="flex-1">
                  <label className="block text-xs font-title mb-1" style={{ color: 'var(--navy)' }}>Width</label>
                  <input value={lw} onChange={(e) => setLw(e.target.value)} placeholder="e.g. 100% or 320px" data-testid={`layout-w-${imgKey}`} className="w-full px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-title mb-1" style={{ color: 'var(--navy)' }}>Height</label>
                  <input value={lh} onChange={(e) => setLh(e.target.value)} placeholder="e.g. 240px" data-testid={`layout-h-${imgKey}`} className="w-full px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={saveLayout} disabled={busy} data-testid={`layout-save-${imgKey}`} className="px-3 py-2 rounded text-sm font-title" style={{ background: 'var(--navy)', color: 'var(--cream)', opacity: busy ? 0.6 : 1 }}>Save display</button>
                {layout && <button onClick={resetLayout} disabled={busy} data-testid={`layout-reset-${imgKey}`} className="text-sm px-3 py-2 rounded" style={{ background: 'var(--sage)' }}>Reset display</button>}
              </div>
            </div>

            {/* --- Rotate by tag --- */}
            <div className="rounded-lg p-3 mb-4" style={{ background: 'rgba(122,86,44,0.08)', border: '1px solid rgba(122,86,44,0.25)' }}>
              <div className="flex items-center gap-1.5 mb-1">
                <Shuffle size={15} style={{ color: 'var(--maroon)' }} />
                <span className="font-title text-sm" style={{ color: 'var(--maroon)' }}>Randomly rotate from a tag</span>
              </div>
              <p className="text-xs mb-2" style={{ color: 'var(--olive)' }}>
                Shows a random approved gallery photo carrying the chosen tag. A new one is picked on each page load.
              </p>
              {tags.length === 0 ? (
                <p className="text-xs" style={{ color: 'var(--olive)' }}>No tagged, approved photos yet. Tag some shared photos in the gallery first.</p>
              ) : (
                <>
                  <label className="block text-xs font-title mb-1" style={{ color: 'var(--navy)' }}>Tag</label>
                  <select
                    value={pickTag}
                    onChange={(e) => setPickTag(e.target.value)}
                    data-testid={`rotation-tag-select-${imgKey}`}
                    className="w-full px-3 py-2 rounded border text-sm mb-2 bg-white"
                    style={{ borderColor: 'rgba(0,0,0,0.2)' }}
                  >
                    <option value="">— Choose a tag —</option>
                    {tags.map((t) => (
                      <option key={t.tag} value={t.tag}>{`#${t.tag} (${t.count})`}</option>
                    ))}
                  </select>
                  <label className="block text-xs font-title mb-1" style={{ color: 'var(--navy)' }}>Change</label>
                  <select
                    value={pickInterval}
                    onChange={(e) => setPickInterval(Number(e.target.value))}
                    data-testid={`rotation-interval-select-${imgKey}`}
                    className="w-full px-3 py-2 rounded border text-sm mb-3 bg-white"
                    style={{ borderColor: 'rgba(0,0,0,0.2)' }}
                  >
                    {INTERVALS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <div className="flex items-center gap-2">
                    <button onClick={saveRotation} disabled={busy || !pickTag} data-testid={`rotation-save-${imgKey}`}
                      className="px-3 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: (busy || !pickTag) ? 0.6 : 1 }}>
                      Save rotation
                    </button>
                    {rotation && (
                      <button onClick={turnOffRotation} disabled={busy} data-testid={`rotation-off-${imgKey}`}
                        className="text-sm px-3 py-2 rounded" style={{ background: 'var(--sage)' }}>
                        Turn off rotation
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>

            <label className="text-sm flex items-center gap-1 mb-1"><LinkIcon size={14} /> Or paste an image URL</label>
            <div className="flex gap-2 mb-4">
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://..."
                data-testid={`image-url-input-${imgKey}`}
                className="flex-1 px-3 py-2 rounded border text-sm"
                style={{ borderColor: 'rgba(0,0,0,0.2)' }}
              />
              <button onClick={saveUrl} disabled={busy} data-testid={`image-url-save-${imgKey}`} className="px-3 py-2 rounded text-sm font-title" style={{ background: 'var(--navy)', color: 'var(--cream)' }}>Save</button>
            </div>

            <div className="flex items-center gap-3 my-3">
              <div className="flex-1 h-px" style={{ background: 'rgba(0,0,0,0.12)' }} />
              <span className="text-xs" style={{ color: 'var(--olive)' }}>or</span>
              <div className="flex-1 h-px" style={{ background: 'rgba(0,0,0,0.12)' }} />
            </div>

            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`image-file-input-${imgKey}`} />
            <button onClick={() => fileRef.current && fileRef.current.click()} disabled={busy} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded border text-sm font-title" style={{ borderColor: 'rgba(0,0,0,0.2)' }}>
              <Upload size={15} /> Upload from computer
            </button>
            <button onClick={() => setGalleryOpen(true)} disabled={busy} data-testid={`image-gallery-${imgKey}`} className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2.5 rounded border text-sm font-title" style={{ borderColor: 'rgba(0,0,0,0.2)' }}>
              <Images size={15} /> Choose from gallery
            </button>
            <GalleryPicker open={galleryOpen} onClose={() => setGalleryOpen(false)} onSelect={async (u) => { setBusy(true); try { await setImageOverride(imgKey, u, { clearRotation: true }); setOpen(false); } finally { setBusy(false); } }} />

            {err && <p className="text-sm mt-3" style={{ color: '#8a1f11' }}>{err}</p>}

            <div className="flex justify-between mt-5">
              {override ? (
                <button onClick={reset} disabled={busy} className="flex items-center gap-1 text-sm" style={{ color: 'var(--olive)' }}>
                  <RotateCcw size={14} /> Reset to default
                </button>
              ) : <span />}
              <button onClick={() => setOpen(false)} disabled={busy} className="text-sm px-3 py-1.5 rounded" style={{ background: 'var(--sage)' }}>Close</button>
            </div>
            {busy && <p className="text-xs mt-2" style={{ color: 'var(--olive)' }}>Working…</p>}
          </div>
        </div>
      )}
    </div>
  );
}
